/**
 * Marketing site header: Solutions/Resources dropdowns, main links, auth-aware
 * CTAs, and the mobile overlay menu (marketing-mobile-menu.tsx).
 *
 * CSS RESILIENCE: Critical layout dimensions use inline styles to survive CSS chunk
 * ordering issues in production builds.
 */

'use client';

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback
} from 'react';
import Link from 'next/link';
import Image from 'next/image';

import {
  BarChart3,
  BookOpen,
  ChevronDown,
  GraduationCap,
  Globe,
  Handshake,
  Map,
  MonitorPlay,
  Scale,
  School,
  ShieldCheck,
  Users,
  Building2,
  Wrench
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Menu } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MktgButton } from '@/components/marketing/mktg-button';
import { MarketingMobileMenu } from '@/components/marketing/marketing-mobile-menu';

// Layout effect on the client so breakpoint values apply before first paint
// (no mobile→desktop flash); useEffect on the server, where it is a no-op.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const solutionsItems = [
  {
    href: '/for-counselors',
    label: 'For Counselors',
    icon: GraduationCap,
    description: 'Spend more time with students'
  },
  {
    href: '/for-schools',
    label: 'For Schools',
    icon: School,
    description: 'Reduce admin, improve outcomes'
  },
  {
    href: '/for-students',
    label: 'For Students',
    icon: Users,
    description: 'Discover your career path'
  },
  {
    href: '/for-industry',
    label: 'For Industry',
    icon: Building2,
    description: 'Connect with future talent'
  },
  {
    href: '/industry-partnerships',
    label: 'Industry Partnerships',
    icon: Handshake,
    description: 'Pre-vetted opportunities for students'
  },
  {
    href: '/international-school-career-readiness-platform',
    label: 'For International Schools',
    icon: Globe,
    description: 'Career readiness for global schools'
  }
];

/** Top-of-funnel SEO/lead-gen assets, kept reachable in ≤2 clicks from every page. */
const resourcesItems = [
  {
    href: '/toolkit',
    label: 'Free Toolkit',
    icon: Wrench,
    description: 'Calculators, templates & quizzes'
  },
  {
    href: '/blog',
    label: 'Blog',
    icon: BookOpen,
    description: 'Guides for counselors & school leaders'
  },
  {
    href: '/case-studies',
    label: 'Case Studies',
    icon: BarChart3,
    description: 'Results from schools on TEX'
  },
  {
    href: '/compare',
    label: 'Compare TEX',
    icon: Scale,
    description: 'TEX vs Naviance, Xello & more'
  },
  {
    href: '/solutions',
    label: 'Solutions by Region',
    icon: Map,
    description: 'UK, Australia, UAE, international & more'
  },
  {
    href: '/product-tour',
    label: 'Product Tour',
    icon: MonitorPlay,
    description: 'Self-guided platform walkthrough'
  },
  {
    href: '/security',
    label: 'Security & Compliance',
    icon: ShieldCheck,
    description: 'FERPA Ready, GDPR aligned, safeguarding'
  }
];

const mainNavLinks = [
  { href: '/features', label: 'Features' },
  { href: '/pricing', label: 'Pricing' }
];

/**
 * Mobile menu shows ONLY these (max 3) plus the CTAs, so the overlay never
 * scrolls — the no-scroll budget in marketing-mobile-menu.tsx is what keeps
 * iOS WebKit from mispainting it. Evaluation intent (Features), commercial
 * intent (Pricing), and the phone-friendly demo substitute (Product Tour);
 * every other page stays reachable via desktop nav + footer.
 */
const mobileNavItems = [
  { href: '/features', label: 'Features' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/product-tour', label: 'Product Tour' }
];

interface NavDropdownItem {
  href: string;
  label: string;
  icon: LucideIcon;
  description: string;
}

/**
 * Desktop nav dropdown — owns its open state, Escape handling, and
 * click-outside dismissal so multiple dropdowns can coexist without
 * cross-talk.
 */
function NavDropdown({
  label,
  items
}: {
  label: string;
  items: NavDropdownItem[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        close();
      }
    };

    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open, close]);

  return (
    <div ref={ref} className='relative'>
      <button
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        aria-expanded={open}
        aria-haspopup='true'
        className='flex items-center gap-1 text-base font-medium transition-colors'
        style={{
          color: 'var(--mktg-dark)',
          fontFamily: 'var(--font-body)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0
        }}
      >
        {label}
        <ChevronDown
          className='h-4 w-4 transition-transform'
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            color: 'var(--mktg-gray)'
          }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -4 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            onMouseLeave={close}
            className='absolute top-full left-0 mt-2 w-64 overflow-hidden rounded-2xl shadow-lg'
            style={{
              backgroundColor: 'var(--mktg-white)',
              border: '1px solid var(--mktg-card-border, #f0f0f0)'
            }}
            role='menu'
          >
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  role='menuitem'
                  onClick={close}
                  className='flex items-start gap-3 px-4 py-3 transition-colors hover:bg-gray-50'
                >
                  <Icon
                    className='mt-0.5 h-4 w-4 flex-shrink-0'
                    style={{ color: 'var(--mktg-blue)' }}
                  />
                  <div>
                    <div
                      className='text-sm font-semibold'
                      style={{ color: 'var(--mktg-dark)' }}
                    >
                      {item.label}
                    </div>
                    <div
                      className='text-xs'
                      style={{ color: 'var(--mktg-gray)' }}
                    >
                      {item.description}
                    </div>
                  </div>
                </Link>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface MarketingHeaderProps {
  isAuthenticated: boolean;
  isLoading: boolean;
}

export function MarketingHeader({
  isAuthenticated,
  isLoading
}: MarketingHeaderProps) {
  const headerRef = useRef<HTMLElement | null>(null);
  const [elevated, setElevated] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  // JS breakpoints for layout-critical behavior (immune to CSS ordering).
  // Initialised to false to match SSR output.
  const [isMd, setIsMd] = useState(false);
  const [isSm, setIsSm] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const mqMd = window.matchMedia('(min-width: 768px)');
    const mqSm = window.matchMedia('(min-width: 640px)');
    setMounted(true);
    setIsMd(mqMd.matches);
    setIsSm(mqSm.matches);
    const handlerMd = (e: MediaQueryListEvent) => setIsMd(e.matches);
    const handlerSm = (e: MediaQueryListEvent) => setIsSm(e.matches);
    mqMd.addEventListener('change', handlerMd);
    mqSm.addEventListener('change', handlerSm);
    return () => {
      mqMd.removeEventListener('change', handlerMd);
      mqSm.removeEventListener('change', handlerSm);
    };
  }, []);

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) return;

    const mainElement = document.querySelector('main');
    const firstSection = mainElement?.firstElementChild as Element | null;

    let cleanup: (() => void) | undefined;

    if (firstSection && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          setElevated(!entry.isIntersecting);
        },
        { threshold: 0.99 }
      );
      observer.observe(firstSection);
      cleanup = () => observer.disconnect();
    } else {
      const handleScroll = () => {
        setElevated(window.scrollY > 0);
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
      cleanup = () => window.removeEventListener('scroll', handleScroll);
    }

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // Marketing header always has a white background, so always use the light logo
  // (dark text on transparent) regardless of system theme.
  const logoSrc = '/logos/tex/tex-rectangle-light-logo.png';

  return (
    <header
      ref={headerRef}
      className='sticky top-0 z-50'
      style={{ backgroundColor: 'var(--mktg-white)' }}
    >
      <div
        className='mx-auto flex items-center justify-between transition-shadow'
        style={{
          maxWidth: 'var(--mktg-container-max, 1440px)',
          height: isMd ? '92px' : '64px',
          padding: isMd ? '0 var(--mktg-container-px, 72px)' : '0 24px',
          boxShadow: elevated
            ? '0 2px 12px 0 rgba(0,0,0,0.08)'
            : '0 1px 0 0 var(--mktg-card-border, #f0f0f0)'
        }}
      >
        <Link
          href='/'
          aria-label='TEX Edu'
          className='inline-flex items-center gap-1'
        >
          {mounted && (
            <Image
              src={logoSrc}
              alt='TEX Edu'
              width={75}
              height={64}
              priority
              className='object-contain'
            />
          )}
          <span
            className='mktg-body text-2xl font-semibold'
            style={{
              display: isSm ? 'block' : 'none',
              color: 'var(--mktg-dark)',
              fontFamily: 'var(--font-body)'
            }}
          >
            Edu
          </span>
        </Link>

        <nav
          aria-label='Primary'
          className='items-center gap-8'
          style={{ display: isMd ? 'flex' : 'none' }}
        >
          <NavDropdown label='Solutions' items={solutionsItems} />
          <NavDropdown label='Resources' items={resourcesItems} />

          {mainNavLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className='text-base font-medium transition-colors hover:opacity-70'
              style={{
                color: 'var(--mktg-dark)',
                fontFamily: 'var(--font-body)'
              }}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className='flex items-center gap-3'>
          {!isLoading && isAuthenticated && (
            <MktgButton variant='primary' size='sm' href='/dashboard'>
              Dashboard
            </MktgButton>
          )}

          {!isLoading && !isAuthenticated && (
            <div
              className='items-center gap-3'
              style={{ display: isMd ? 'flex' : 'none' }}
            >
              <MktgButton variant='secondary' size='sm' href='/auth/login'>
                Log in
              </MktgButton>
              <MktgButton variant='secondary' size='sm' href='/auth/register'>
                Sign Up
              </MktgButton>
              <MktgButton variant='primary' size='sm' href='/request-demo'>
                Book a Demo
              </MktgButton>
            </div>
          )}

          <button
            type='button'
            onClick={() => setMobileOpen(true)}
            aria-label='Open navigation menu'
            aria-expanded={mobileOpen}
            style={{
              display: isMd ? 'none' : 'inline-flex',
              background: 'none',
              border: '1px solid var(--mktg-card-border, #f0f0f0)',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              color: 'var(--mktg-dark, #1e1e1e)'
            }}
          >
            <Menu className='h-5 w-5' />
          </button>

          <MarketingMobileMenu
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            showCtas={!isLoading && !isAuthenticated}
            logoSrc={logoSrc}
            showLogo={mounted}
            navItems={mobileNavItems}
          />
        </div>
      </div>
    </header>
  );
}
